/**
 * Utility functions for Copy & Paste / Import feature of Company Creation Form
 * Version: CRM_COMPANY_REQUIREMENT_V1
 */

export const FORMAT_HEADER = '--- CRM COMPANY REQUIREMENT V1 ---';
export const FORMAT_FOOTER = '-----------------------------------';

/**
 * Sanitize string input to prevent XSS / malicious HTML injection
 */
export const sanitizeText = (str) => {
  if (typeof str !== 'string') return '';
  return str.replace(/<[^>]*>?/gm, '').trim();
};

/**
 * Generate human-readable text from company form state
 */
export const generateCompanyCopyText = (params = {}) => {
  const { form = {} } = params || {};

  const companyName = form.companyName || '';
  const industries = form.industries || '';
  const companyAddress = form.companyAddress || '';
  const area = form.area || '';
  const city = form.city || '';
  const state = form.state || '';
  const country = form.country || '';
  const pincode = form.pincode || '';
  const contactPerson = form.contactPerson || '';
  const contactPersonDesignation = form.contactPersonDesignation || '';
  const contactNumber = form.contactNumber || '';
  const email = form.email || '';
  const contactPerson2 = form.contactPerson2 || '';
  const contactPerson2Designation = form.contactPerson2Designation || '';
  const contactNumber2 = form.contactNumber2 || '';
  const email2 = form.email2 || '';
  const websiteUrl = form.websiteUrl || '';
  const gpsLocation = form.gpsLocation || '';
  const agreementStartDate = form.agreementStartDate || '';
  const agreementEndDate = form.agreementEndDate || '';
  const invoiceNumber = form.invoiceNumber || '';
  const paymentMode = form.paymentMode || '';
  const paymentRemark = form.paymentRemark || '';
  const tokenAmount = form.tokenAmount != null ? String(form.tokenAmount) : '';

  const lines = [
    FORMAT_HEADER,
    `Company Name: ${companyName}`,
    `Industry: ${industries}`,
    `Address: ${companyAddress}`,
    `Area: ${area}`,
    `City: ${city}`,
    `State: ${state}`,
    `Country: ${country}`,
    `Pincode: ${pincode}`,
    `Primary Contact Person: ${contactPerson}`,
    `Primary Designation: ${contactPersonDesignation}`,
    `Primary Phone: ${contactNumber}`,
    `Primary Email: ${email}`,
    `Secondary Contact Person: ${contactPerson2}`,
    `Secondary Designation: ${contactPerson2Designation}`,
    `Secondary Phone: ${contactNumber2}`,
    `Secondary Email: ${email2}`,
    `Website: ${websiteUrl}`,
    `GPS Location: ${gpsLocation}`,
    `Agreement Start Date: ${agreementStartDate}`,
    `Agreement End Date: ${agreementEndDate}`,
    `Invoice Number: ${invoiceNumber}`,
    `Payment Mode: ${paymentMode}`,
    `Payment Remark: ${paymentRemark}`,
    `Token Amount (₹): ${tokenAmount}`,
    FORMAT_FOOTER,
  ];

  return lines.join('\n');
};

/**
 * Field Alias Dictionary for Company Form Parsing
 */
export const COMPANY_FIELD_ALIASES = {
  companyName: ['company name', 'company', 'organization', 'organization name', 'firm name', 'firm'],
  industries: ['industry', 'industries', 'category', 'sector', 'business category', 'domain'],
  companyAddress: ['address', 'company address', 'office address', 'registered address', 'location address'],
  area: ['area', 'locality', 'suburb', 'landmark'],
  city: ['city', 'town'],
  state: ['state', 'province'],
  country: ['country', 'nation'],
  pincode: ['pincode', 'pin code', 'zipcode', 'zip code', 'zip', 'postal code'],
  contactPerson: ['primary contact person', 'contact person', 'primary contact', 'contact name', 'person name', 'owner name'],
  contactPersonDesignation: ['primary designation', 'contact designation', 'designation', 'person designation', 'role', 'title'],
  contactNumber: ['primary phone', 'contact number', 'phone', 'mobile', 'mobile number', 'contact phone', 'phone number'],
  email: ['primary email', 'email', 'email address', 'contact email', 'official email'],
  contactPerson2: ['secondary contact person', 'contact person 2', 'secondary contact', 'contact 2 name', 'alternate contact person', 'second contact'],
  contactPerson2Designation: ['secondary designation', 'contact designation 2', 'designation 2', 'secondary role'],
  contactNumber2: ['secondary phone', 'contact number 2', 'phone 2', 'mobile 2', 'contact phone 2', 'alternate phone', 'alternate mobile'],
  email2: ['secondary email', 'email 2', 'alternate email', 'contact email 2'],
  websiteUrl: ['website', 'website url', 'url', 'company website', 'site', 'site url'],
  gpsLocation: ['gps location', 'gps link', 'google maps link', 'maps link', 'map link', 'location link', 'google map link'],
  agreementStartDate: ['agreement start date', 'start date', 'contract start date', 'agreement date'],
  agreementEndDate: ['agreement end date', 'end date', 'contract end date', 'expiry date'],
  invoiceNumber: ['invoice number', 'invoice no', 'inv no', 'bill number'],
  paymentMode: ['payment mode', 'payment method', 'mode of payment', 'pay mode'],
  paymentRemark: ['payment remark', 'payment details', 'payment note', 'remark', 'payment remarks'],
  tokenAmount: ['token amount', 'token amount (₹)', 'token amount (rs)', 'token', 'amount', 'advance amount'],
};

/**
 * Parse raw text into structured company object and generate diff comparison
 */
export const parseCompanyImportText = (rawText, currentFormState = {}) => {
  const warnings = [];
  const parsedData = {};
  if (!rawText || typeof rawText !== 'string') {
    return { parsedData: {}, diff: [], warnings: ['Empty text provided'], hasFields: false };
  }

  const lines = rawText.split(/\r?\n/);
  const rawPairs = {};

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('---')) return;

    const colonIndex = trimmed.indexOf(':');
    if (colonIndex === -1) return;

    const keyPart = trimmed.substring(0, colonIndex).trim().toLowerCase();
    const valPart = trimmed.substring(colonIndex + 1).trim();

    if (keyPart && valPart) {
      rawPairs[keyPart] = valPart;
    }
  });

  // Match raw pairs against aliases
  Object.keys(COMPANY_FIELD_ALIASES).forEach((fieldKey) => {
    const aliases = COMPANY_FIELD_ALIASES[fieldKey];
    for (const alias of aliases) {
      if (rawPairs[alias] !== undefined) {
        parsedData[fieldKey] = sanitizeText(rawPairs[alias]);
        break;
      }
    }
  });

  // Validations & Format conversions
  if (parsedData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parsedData.email)) {
    warnings.push(`Invalid primary email format: "${parsedData.email}"`);
  }
  if (parsedData.email2 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parsedData.email2)) {
    warnings.push(`Invalid secondary email format: "${parsedData.email2}"`);
  }

  if (parsedData.contactNumber) {
    const cleanPhone = parsedData.contactNumber.replace(/\D/g, '');
    if (cleanPhone.length === 10) {
      parsedData.contactNumber = cleanPhone;
    } else {
      warnings.push(`Primary phone should be 10 digits: "${parsedData.contactNumber}"`);
    }
  }

  if (parsedData.contactNumber2) {
    const cleanPhone2 = parsedData.contactNumber2.replace(/\D/g, '');
    if (cleanPhone2.length === 10) {
      parsedData.contactNumber2 = cleanPhone2;
    } else if (parsedData.contactNumber2.trim()) {
      warnings.push(`Secondary phone should be 10 digits: "${parsedData.contactNumber2}"`);
    }
  }

  if (parsedData.tokenAmount !== undefined && parsedData.tokenAmount !== '') {
    const cleanToken = parsedData.tokenAmount.replace(/[^0-9.]/g, '');
    if (!isNaN(parseFloat(cleanToken))) {
      parsedData.tokenAmount = cleanToken;
    } else {
      warnings.push(`Invalid token amount: "${parsedData.tokenAmount}"`);
      delete parsedData.tokenAmount;
    }
  }

  if (parsedData.websiteUrl && !/^(https?:\/\/|www\.)[^\s]+$/.test(parsedData.websiteUrl)) {
    warnings.push(`Invalid website URL format: "${parsedData.websiteUrl}"`);
  }

  if (parsedData.gpsLocation && !/^(https?:\/\/|www\.)[^\s]+$/.test(parsedData.gpsLocation)) {
    warnings.push(`Invalid GPS link format: "${parsedData.gpsLocation}"`);
  }

  const fieldLabels = [
    { key: 'companyName', label: 'Company Name' },
    { key: 'industries', label: 'Industry' },
    { key: 'companyAddress', label: 'Address' },
    { key: 'area', label: 'Area' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'country', label: 'Country' },
    { key: 'pincode', label: 'Pincode' },
    { key: 'contactPerson', label: 'Primary Contact Person' },
    { key: 'contactPersonDesignation', label: 'Primary Designation' },
    { key: 'contactNumber', label: 'Primary Phone' },
    { key: 'email', label: 'Primary Email' },
    { key: 'contactPerson2', label: 'Secondary Contact Person' },
    { key: 'contactPerson2Designation', label: 'Secondary Designation' },
    { key: 'contactNumber2', label: 'Secondary Phone' },
    { key: 'email2', label: 'Secondary Email' },
    { key: 'websiteUrl', label: 'Website' },
    { key: 'gpsLocation', label: 'GPS Location' },
    { key: 'agreementStartDate', label: 'Agreement Start Date' },
    { key: 'agreementEndDate', label: 'Agreement End Date' },
    { key: 'invoiceNumber', label: 'Invoice Number' },
    { key: 'paymentMode', label: 'Payment Mode' },
    { key: 'paymentRemark', label: 'Payment Remark' },
    { key: 'tokenAmount', label: 'Token Amount (₹)' },
  ];

  const diff = fieldLabels.map(({ key, label }) => {
    const currentVal = currentFormState[key] != null ? String(currentFormState[key]).trim() : '';
    const importedVal = parsedData[key] !== undefined ? String(parsedData[key]).trim() : undefined;

    let status = 'unchanged';
    if (importedVal === undefined) {
      status = 'missing';
    } else if (currentVal !== importedVal) {
      status = 'changed';
    }

    return {
      key,
      label,
      currentVal,
      importedVal: importedVal !== undefined ? importedVal : '— (Keep Existing)',
      status,
    };
  });

  const hasFields = Object.keys(parsedData).length > 0;

  return {
    parsedData,
    diff,
    warnings,
    hasFields,
  };
};
